import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Text } from "../../../../components/ui";
import { diffLines } from "../../../../utils/text";

export interface SkillDiffProps {
  /** The skill's instructions as they are now. */
  current: string;
  /** The instructions after the change — for an amendment, current plus the appended section. */
  proposed: string;
}

const LINE_BG: Record<"same" | "added" | "removed", string | undefined> = {
  same: undefined,
  added: "var(--diff-added-bg, rgba(34, 197, 94, 0.18))",
  removed: "var(--diff-removed-bg, rgba(239, 68, 68, 0.18))",
};

/**
 * Current instructions on the left, proposed on the right, lines that differ highlighted — the
 * review an admin needs before letting an analytics job touch a skill that works. Each column shows
 * its own full text; the highlight is only where the two differ.
 */
const SkillDiff: React.FC<SkillDiffProps> = ({ current, proposed }) => {
  const { t } = useTranslation();
  const lines = diffLines(current, proposed);
  const left = lines.filter((l) => l.kind !== "added");
  const right = lines.filter((l) => l.kind !== "removed");

  const column = (title: string, rows: typeof lines): React.ReactNode => (
    <Box
      flex="1"
      minW={0}
      borderWidth="1px"
      borderColor="var(--code-block-border)"
      borderRadius="6px"
      bg="var(--code-block-bg)"
      overflow="hidden"
    >
      <Text
        fontSize="xs"
        fontWeight={600}
        px={3}
        py={1}
        color="var(--text-primary)"
        borderBottomWidth="1px"
        borderColor="var(--code-block-border)"
      >
        {title}
      </Text>
      <Box px={3} py={2} maxH="360px" overflowY="auto">
        {rows.length === 0 ? (
          <Text fontSize="xs" color="var(--text-secondary-color)">
            {t("app.suggestions.preview.diff.empty")}
          </Text>
        ) : (
          rows.map((line, i) => (
            <Text
              key={i}
              fontSize="xs"
              fontFamily="mono"
              whiteSpace="pre-wrap"
              color="var(--text-primary)"
              bg={LINE_BG[line.kind]}
              px={1}
              mx={-1}
              borderRadius="2px"
            >
              {line.text.length > 0 ? line.text : " "}
            </Text>
          ))
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      display="flex"
      gap={2}
      w="100%"
      flexDirection={{ base: "column", lg: "row" }}
    >
      {column(t("app.suggestions.preview.diff.current"), left)}
      {column(t("app.suggestions.preview.diff.proposed"), right)}
    </Box>
  );
};

export default SkillDiff;
